/**
 * Flow Diff Engine
 * Compares two flows (nodes + edges) and produces a structured diff result.
 * 
 * Key challenge: Node IDs are regenerated on every save (RegenerateFlowDetailsIds),
 * so we use structural matching (nodeType + position proximity + data similarity)
 * instead of ID matching.
 */

import { getNodeTypeLabel } from './flowUtils';
import { diffWordsWithSpace } from 'diff';

// ─── Helpers ────────────────────────────────────────────────

/**
 * Euclidean distance between two {x, y} positions
 */
function posDistance(a, b) {
    if (!a || !b) return Infinity;
    return Math.sqrt(Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2));
}

/**
 * Shallow-equal two primitive values (string, number, boolean, null, undefined)
 */
function primEqual(a, b) {
    if (a === b) return true;
    if (a == null && b == null) return true;
    // Consider empty string and null as equivalent
    if ((a === '' || a === null || a === undefined) && (b === '' || b === null || b === undefined)) return true;
    return false;
}

/**
 * Deep-equal check for JSON-serializable values
 */
function deepEqual(a, b) {
    if (a === b) return true;
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;
    if (typeof a !== typeof b) return false;
    if (typeof a !== 'object') return a === b;
    if (Array.isArray(a) !== Array.isArray(b)) return false;
    if (Array.isArray(a)) {
        if (a.length !== b.length) return false;
        return a.every((v, i) => deepEqual(v, b[i]));
    }
    const keysA = Object.keys(a).sort();
    const keysB = Object.keys(b).sort();
    if (keysA.length !== keysB.length) return false;
    return keysA.every((k, i) => k === keysB[i] && deepEqual(a[k], b[k]));
}

/**
 * Compute similarity score [0..1] between two node data objects.
 * Ignores label, position-related fields, and internal IDs.
 */
function dataSimilarity(dataA, dataB) {
    if (!dataA || !dataB) return 0;
    
    // Exclude meta/display fields from comparison
    const IGNORE_KEYS = new Set(['label', 'type', 'selectedModel', 'modelDisplayId']);
    
    const keysA = Object.keys(dataA).filter(k => !IGNORE_KEYS.has(k));
    const keysB = Object.keys(dataB).filter(k => !IGNORE_KEYS.has(k));
    const allKeys = new Set([...keysA, ...keysB]);
    
    if (allKeys.size === 0) return 1;
    
    let matchCount = 0;
    for (const key of allKeys) {
        if (deepEqual(dataA[key], dataB[key])) {
            matchCount++;
        }
    }
    return matchCount / allKeys.size;
}

// ─── Node Matching ──────────────────────────────────────────

/**
 * Compute a match score between two nodes.
 * @returns {number} Score from 0 (no match) to 1 (perfect match)
 */
function nodeMatchScore(nodeA, nodeB) {
    // Type must match - if different types, no match possible
    const typeA = nodeA.data?.type || nodeA.nodeType;
    const typeB = nodeB.data?.type || nodeB.nodeType;
    if (typeA !== typeB) return 0;

    let score = 0;
    let weights = 0;

    // Type match: weight 0.3
    score += 0.3;
    weights += 0.3;

    // Label match: weight 0.2
    const labelA = nodeA.data?.label || '';
    const labelB = nodeB.data?.label || '';
    if (labelA && labelB) {
        if (labelA === labelB) {
            score += 0.2;
        } else if (labelA.toLowerCase() === labelB.toLowerCase()) {
            score += 0.15;
        }
    } else if (!labelA && !labelB) {
        score += 0.1; // both empty — mild match
    }
    weights += 0.2;

    // Position proximity: weight 0.2 (closer = higher score)
    const dist = posDistance(nodeA.position, nodeB.position);
    if (dist < 20) {
        score += 0.2;
    } else if (dist < 100) {
        score += 0.15;
    } else if (dist < 300) {
        score += 0.1;
    } else if (dist < 600) {
        score += 0.05;
    }
    weights += 0.2;

    // Data similarity: weight 0.3
    const dataSim = dataSimilarity(nodeA.data, nodeB.data);
    score += 0.3 * dataSim;
    weights += 0.3;

    return weights > 0 ? score / weights : 0;
}

/**
 * Match nodes between left and right flow using greedy best-match algorithm.
 * Uses nodeType + label + position + data similarity.
 * 
 * @param {Array} leftNodes - Nodes from left flow
 * @param {Array} rightNodes - Nodes from right flow
 * @returns {{ matched: Array<{leftId, rightId, score}>, leftOnly: Array<string>, rightOnly: Array<string> }}
 */
export function matchNodes(leftNodes, rightNodes) {
    const MATCH_THRESHOLD = 0.3; // Minimum score to consider a match

    // Build score matrix
    const scores = [];
    for (let i = 0; i < leftNodes.length; i++) {
        for (let j = 0; j < rightNodes.length; j++) {
            const score = nodeMatchScore(leftNodes[i], rightNodes[j]);
            if (score >= MATCH_THRESHOLD) {
                scores.push({ leftIdx: i, rightIdx: j, score });
            }
        }
    }

    // Sort by score descending (greedy best-match)
    scores.sort((a, b) => b.score - a.score);

    const matchedLeft = new Set();
    const matchedRight = new Set();
    const matched = [];

    for (const { leftIdx, rightIdx, score } of scores) {
        if (matchedLeft.has(leftIdx) || matchedRight.has(rightIdx)) continue;
        matched.push({
            leftId: leftNodes[leftIdx].id,
            rightId: rightNodes[rightIdx].id,
            score
        });
        matchedLeft.add(leftIdx);
        matchedRight.add(rightIdx);
    }

    const leftOnly = leftNodes
        .filter((_, i) => !matchedLeft.has(i))
        .map(n => n.id);
    const rightOnly = rightNodes
        .filter((_, i) => !matchedRight.has(i))
        .map(n => n.id);

    return { matched, leftOnly, rightOnly };
}

// ─── Node Data Diff ─────────────────────────────────────────

/**
 * Keys to skip when diffing node data (internal/display-only fields)
 */
const SKIP_DIFF_KEYS = new Set([
    'type', 'label', 'selectedModel', // display duplicates
]);

/**
 * Diff data of two matched nodes field-by-field.
 * @param {Object} leftData - Left node's data object
 * @param {Object} rightData - Right node's data object
 * @returns {{ unchanged: Array<string>, modified: Array<{field, left, right}>, leftOnly: Array<{field, value}>, rightOnly: Array<{field, value}> }}
 */
export function diffNodeData(leftData, rightData) {
    const result = {
        unchanged: [],
        modified: [],
        leftOnly: [],
        rightOnly: []
    };

    const leftKeys = Object.keys(leftData || {}).filter(k => !SKIP_DIFF_KEYS.has(k));
    const rightKeys = Object.keys(rightData || {}).filter(k => !SKIP_DIFF_KEYS.has(k));
    const allKeys = new Set([...leftKeys, ...rightKeys]);

    for (const key of allKeys) {
        const inLeft = leftData && key in leftData;
        const inRight = rightData && key in rightData;

        if (inLeft && inRight) {
            if (deepEqual(leftData[key], rightData[key])) {
                result.unchanged.push(key);
            } else {
                result.modified.push({
                    field: key,
                    left: leftData[key],
                    right: rightData[key]
                });
            }
        } else if (inLeft && !inRight) {
            // Present only on left — if value is null/empty/default, treat as unchanged
            if (primEqual(leftData[key], null) || primEqual(leftData[key], false) || primEqual(leftData[key], 0)) {
                result.unchanged.push(key);
            } else {
                result.leftOnly.push({ field: key, value: leftData[key] });
            }
        } else {
            if (primEqual(rightData[key], null) || primEqual(rightData[key], false) || primEqual(rightData[key], 0)) {
                result.unchanged.push(key);
            } else {
                result.rightOnly.push({ field: key, value: rightData[key] });
            }
        }
    }

    return result;
}

// ─── Edge Diff ──────────────────────────────────────────────

/**
 * Normalize an edge using node mapping so we can compare by topology.
 * Returns a canonical string key: "leftSourceType:sourceHandle→leftTargetType"
 */
function normalizeEdge(edge, nodeMap) {
    const sourceNode = nodeMap.get(edge.source);
    const targetNode = nodeMap.get(edge.target);
    const sourceType = sourceNode?.data?.type || 'unknown';
    const targetType = targetNode?.data?.type || 'unknown';
    const handle = edge.sourceHandle || 'output';
    return `${sourceType}:${handle}→${targetType}`;
}

/**
 * Diff edges between two flows using structural comparison.
 * Since node IDs differ between versions, we use the node mapping to compare
 * edges by their source/target node types and handles.
 * 
 * @param {Array} leftEdges
 * @param {Array} rightEdges
 * @param {Array} leftNodes
 * @param {Array} rightNodes
 * @param {Array} nodeMatching - matched pairs from matchNodes()
 * @returns {{ matched: Array, leftOnly: Array, rightOnly: Array }}
 */
export function diffEdges(leftEdges, rightEdges, leftNodes, rightNodes, nodeMatching) {
    // Build node lookup maps
    const leftNodeMap = new Map(leftNodes.map(n => [n.id, n]));
    const rightNodeMap = new Map(rightNodes.map(n => [n.id, n]));

    // Build ID mapping: leftId → rightId and vice versa
    const leftToRight = new Map(nodeMatching.matched.map(m => [m.leftId, m.rightId]));
    const rightToLeft = new Map(nodeMatching.matched.map(m => [m.rightId, m.leftId]));

    // For matched nodes, compare edges by mapped source→target
    const leftEdgeKeys = new Map();
    for (const edge of leftEdges) {
        const mappedTarget = leftToRight.get(edge.target);
        const mappedSource = leftToRight.get(edge.source);
        if (mappedSource && mappedTarget) {
            const key = `${mappedSource}:${edge.sourceHandle || 'output'}→${mappedTarget}`;
            leftEdgeKeys.set(key, edge);
        } else {
            // Edge involves an unmatched node — it's unique to left
            leftEdgeKeys.set(`left-only-${edge.id}`, edge);
        }
    }

    const rightEdgeKeys = new Map();
    for (const edge of rightEdges) {
        const key = `${edge.source}:${edge.sourceHandle || 'output'}→${edge.target}`;
        rightEdgeKeys.set(key, edge);
    }

    const matched = [];
    const leftOnly = [];
    const rightOnly = [];
    const matchedRightKeys = new Set();

    for (const [key, leftEdge] of leftEdgeKeys) {
        if (key.startsWith('left-only-')) {
            leftOnly.push(leftEdge);
            continue;
        }
        if (rightEdgeKeys.has(key)) {
            matched.push({ leftEdge, rightEdge: rightEdgeKeys.get(key) });
            matchedRightKeys.add(key);
        } else {
            leftOnly.push(leftEdge);
        }
    }

    for (const [key, rightEdge] of rightEdgeKeys) {
        if (!matchedRightKeys.has(key)) {
            rightOnly.push(rightEdge);
        }
    }

    return { matched, leftOnly, rightOnly };
}

// ─── Main Orchestrator ──────────────────────────────────────

/**
 * @typedef {Object} FlowData
 * @property {string} id - Flow ID
 * @property {string} name - Flow name
 * @property {number} version - Version number
 * @property {string} versionComment - Version comment
 * @property {string} createBy - Creator
 * @property {string} createDate - Creation date
 * @property {Array} nodes - VueFlow nodes
 * @property {Array} edges - VueFlow edges
 */

/**
 * @typedef {Object} FlowDiffResult
 * @property {Object} summary - { addedNodes, removedNodes, modifiedNodes, unchangedNodes, addedEdges, removedEdges, unchangedEdges, totalChanges }
 * @property {Object} nodeMatching - Result from matchNodes
 * @property {Map<string, Object>} nodeDiffs - Map of leftNodeId → diffNodeData result (for modified nodes)
 * @property {Object} edgeDiff - Result from diffEdges
 * @property {Map<string, string>} nodeDiffStates - Map of nodeId → 'added'|'removed'|'modified'|'unchanged' (for BOTH sides)
 */

/**
 * Compute a comprehensive diff between two flows.
 * 
 * @param {FlowData} leftFlow - Left flow (usually older version)
 * @param {FlowData} rightFlow - Right flow (usually newer version)
 * @returns {FlowDiffResult}
 */
export function computeFlowDiff(leftFlow, rightFlow) {
    const leftNodes = leftFlow.nodes || [];
    const rightNodes = rightFlow.nodes || [];
    const leftEdges = leftFlow.edges || [];
    const rightEdges = rightFlow.edges || [];

    // 1. Match nodes
    const nodeMatching = matchNodes(leftNodes, rightNodes);

    // 2. Diff matched node data
    const leftNodeMap = new Map(leftNodes.map(n => [n.id, n]));
    const rightNodeMap = new Map(rightNodes.map(n => [n.id, n]));
    const nodeDiffs = new Map();
    let modifiedCount = 0;
    let unchangedCount = 0;

    for (const { leftId, rightId } of nodeMatching.matched) {
        const leftNode = leftNodeMap.get(leftId);
        const rightNode = rightNodeMap.get(rightId);
        const diff = diffNodeData(leftNode?.data, rightNode?.data);
        
        const hasChanges = diff.modified.length > 0 || diff.leftOnly.length > 0 || diff.rightOnly.length > 0;
        nodeDiffs.set(leftId, {
            ...diff,
            rightId,
            hasChanges,
            changeCount: diff.modified.length + diff.leftOnly.length + diff.rightOnly.length
        });

        if (hasChanges) {
            modifiedCount++;
        } else {
            unchangedCount++;
        }
    }

    // 3. Diff edges
    const edgeDiff = diffEdges(leftEdges, rightEdges, leftNodes, rightNodes, nodeMatching);

    // 4. Build diff state map for all nodes (both sides)
    const nodeDiffStates = new Map();

    // Left-only nodes → removed
    for (const id of nodeMatching.leftOnly) {
        nodeDiffStates.set(id, 'removed');
    }

    // Right-only nodes → added
    for (const id of nodeMatching.rightOnly) {
        nodeDiffStates.set(id, 'added');
    }

    // Matched nodes → modified or unchanged
    for (const { leftId, rightId } of nodeMatching.matched) {
        const diff = nodeDiffs.get(leftId);
        const state = diff?.hasChanges ? 'modified' : 'unchanged';
        nodeDiffStates.set(leftId, state);
        nodeDiffStates.set(rightId, state);
    }

    // 5. Summary
    const summary = {
        addedNodes: nodeMatching.rightOnly.length,
        removedNodes: nodeMatching.leftOnly.length,
        modifiedNodes: modifiedCount,
        unchangedNodes: unchangedCount,
        addedEdges: edgeDiff.rightOnly.length,
        removedEdges: edgeDiff.leftOnly.length,
        unchangedEdges: edgeDiff.matched.length,
        totalChanges: nodeMatching.rightOnly.length + nodeMatching.leftOnly.length + modifiedCount
            + edgeDiff.rightOnly.length + edgeDiff.leftOnly.length
    };

    return {
        summary,
        nodeMatching,
        nodeDiffs,
        edgeDiff,
        nodeDiffStates,
        // Convenience: lookup maps
        leftNodeMap,
        rightNodeMap
    };
}

/**
 * Format a diff value for display.
 * Handles objects/arrays by JSON-stringifying, and truncates long strings.
 * @param {*} value
 * @param {number} maxLen - Max display length
 * @returns {string}
 */
export function formatDiffValue(value, maxLen = 200) {
    if (value === null || value === undefined) return '(empty)';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return String(value);
    if (typeof value === 'string') {
        return value.length > maxLen ? value.substring(0, maxLen) + '...' : value;
    }
    if (Array.isArray(value)) {
        if (value.length === 0) return '(empty array)';
        const json = JSON.stringify(value, null, 2);
        return json.length > maxLen ? json.substring(0, maxLen) + '...' : json;
    }
    if (typeof value === 'object') {
        const json = JSON.stringify(value, null, 2);
        return json.length > maxLen ? json.substring(0, maxLen) + '...' : json;
    }
    return String(value);
}

/**
 * Get a human-readable field label from a camelCase field name.
 * E.g., "systemPrompt" → "System Prompt", "maxTokens" → "Max Tokens"
 */
export function humanizeFieldName(fieldName) {
    return fieldName
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, s => s.toUpperCase())
        .replace(/\s+/g, ' ')
        .trim();
}

// ─── Inline Word-Level Diff (using 'diff' library, same as GPTModelCompare) ──

/**
 * Escape HTML special characters to prevent XSS when using v-html.
 */
function escapeHtml(str) {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Convert a raw value to a full string for diff comparison (no truncation).
 */
function toFullString(value) {
    if (value === null || value === undefined) return '(empty)';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return String(value);
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
        if (value.length === 0) return '(empty array)';
        return JSON.stringify(value, null, 2);
    }
    if (typeof value === 'object') {
        return JSON.stringify(value, null, 2);
    }
    return String(value);
}

/**
 * Run word-level diff using the 'diff' library (diffWordsWithSpace)
 * and build HTML with inline highlights — same approach as GPTModelCompare.
 * @param {'left'|'right'} side - which side to render
 * @param {*} leftVal - raw left value
 * @param {*} rightVal - raw right value
 * @returns {string} HTML string
 */
function buildInlineDiffHtml(side, leftVal, rightVal) {
    const leftStr = toFullString(leftVal);
    const rightStr = toFullString(rightVal);
    if (leftStr === rightStr) return escapeHtml(leftStr);

    const parts = diffWordsWithSpace(leftStr, rightStr);
    let html = '';

    for (const part of parts) {
        const escaped = escapeHtml(part.value).replace(/\n/g, '<br>');

        if (part.removed) {
            if (side === 'left') {
                html += `<span class="diff-del">${escaped}</span>`;
            }
        } else if (part.added) {
            if (side === 'right') {
                html += `<span class="diff-add">${escaped}</span>`;
            }
        } else {
            html += escaped;
        }
    }

    return html || escapeHtml(side === 'left' ? leftStr : rightStr);
}

/**
 * Compute inline diff HTML for the LEFT (base) side.
 * Shows: equal text as-is, deleted text highlighted in red.
 * @param {*} leftVal - raw left value
 * @param {*} rightVal - raw right value
 * @returns {string} HTML string
 */
export function inlineDiffLeft(leftVal, rightVal) {
    return buildInlineDiffHtml('left', leftVal, rightVal);
}

/**
 * Compute inline diff HTML for the RIGHT (compare) side.
 * Shows: equal text as-is, added text highlighted in green.
 * @param {*} leftVal - raw left value
 * @param {*} rightVal - raw right value
 * @returns {string} HTML string
 */
export function inlineDiffRight(leftVal, rightVal) {
    return buildInlineDiffHtml('right', leftVal, rightVal);
}
